import { lazy, Suspense, type ReactElement } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROLE } from '@network-verify/shared';
import ProtectedRoute from './ProtectedRoute';
import RoleRoute from './RoleRoute';
import MainLayout from '../layouts/MainLayout';
import PageLoading from '../components/PageLoading';

const Login = lazy(() => import('../pages/Login'));
const Register = lazy(() => import('../pages/Register'));
const Forbidden = lazy(() => import('../pages/Forbidden'));
const NotFound = lazy(() => import('../pages/NotFound'));

const Dashboard = lazy(() => import('../pages/Dashboard'));
const Profile = lazy(() => import('../pages/Profile'));
const Tutorial = lazy(() => import('../pages/Tutorial'));
const AppsList = lazy(() => import('../pages/AppsList'));
const AppDetail = lazy(() => import('../pages/AppDetail'));
const KeysList = lazy(() => import('../pages/KeysList'));
const GenerateKeys = lazy(() => import('../pages/GenerateKeys'));
const KeyQuery = lazy(() => import('../pages/KeyQuery'));
const DevicesList = lazy(() => import('../pages/DevicesList'));
const VariablesList = lazy(() => import('../pages/VariablesList'));
const AlgorithmsList = lazy(() => import('../pages/AlgorithmsList'));
const BlacklistList = lazy(() => import('../pages/BlacklistList'));
const VerifyLogs = lazy(() => import('../pages/VerifyLogs'));
const AdminLogs = lazy(() => import('../pages/AdminLogs'));
const AdminUsersList = lazy(() => import('../pages/AdminUsersList'));
const AdminUserDetail = lazy(() => import('../pages/AdminUserDetail'));
const ErrorCodes = lazy(() => import('../pages/ErrorCodes'));
const TrialList = lazy(() => import('../pages/TrialList'));
const AgentList = lazy(() => import('../pages/AgentList'));

const WebApi = lazy(() => import('../pages/WebApi'));
const UserRechargeCards = lazy(() => import('../pages/UserRechargeCards'));
const AgentRechargeCards = lazy(() => import('../pages/AgentRechargeCards'));
const AgentCardTypes = lazy(() => import('../pages/AgentCardTypes'));
const AgentAnnouncement = lazy(() => import('../pages/AgentAnnouncement'));

function withSuspense(el: ReactElement): ReactElement {
  return <Suspense fallback={<PageLoading />}>{el}</Suspense>;
}

export const router = createBrowserRouter([
  { path: '/login', element: withSuspense(<Login />) },
  { path: '/register', element: withSuspense(<Register />) },
  { path: '/403', element: withSuspense(<Forbidden />) },

  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: withSuspense(<Dashboard />) },
      { path: 'profile', element: withSuspense(<Profile />) },
      { path: 'tutorial', element: withSuspense(<Tutorial />) },
      { path: 'error-codes', element: withSuspense(<ErrorCodes />) },

      { path: 'apps', element: withSuspense(<AppsList />) },
      { path: 'apps/:id', element: withSuspense(<AppDetail />) },
      { path: 'apps/:id/api', element: withSuspense(<WebApi />) },
      { path: 'apps/:id/variables', element: withSuspense(<VariablesList />) },
      { path: 'apps/:id/algorithms', element: withSuspense(<AlgorithmsList />) },
      { path: 'apps/:id/blacklist', element: withSuspense(<BlacklistList />) },
      { path: 'apps/:id/versions', element: withSuspense(<Tutorial />) },

      { path: 'apps/:id/keys', element: withSuspense(<KeysList />) },
      { path: 'apps/:id/keys/generate', element: withSuspense(<GenerateKeys />) },
      { path: 'apps/:id/keys/query', element: withSuspense(<KeyQuery />) },

      { path: 'apps/:id/trial', element: withSuspense(<TrialList />) },

      { path: 'apps/:id/devices', element: withSuspense(<DevicesList />) },
      { path: 'apps/:id/online', element: withSuspense(<DevicesList />) },

      { path: 'apps/:id/logs', element: withSuspense(<VerifyLogs />) },
      { path: 'logs', element: withSuspense(<AdminLogs />) },

      {
        path: 'admin/users',
        element: (
          <RoleRoute roles={[ROLE.ADMIN]}>
            {withSuspense(<AdminUsersList />)}
          </RoleRoute>
        ),
      },
      {
        path: 'admin/users/:id',
        element: (
          <RoleRoute roles={[ROLE.ADMIN]}>
            {withSuspense(<AdminUserDetail />)}
          </RoleRoute>
        ),
      },
      {
        path: 'admin/users/cards',
        element: (
          <RoleRoute roles={[ROLE.ADMIN]}>
            {withSuspense(<UserRechargeCards />)}
          </RoleRoute>
        ),
      },
      {
        path: 'admin/agents',
        element: (
          <RoleRoute roles={[ROLE.ADMIN]}>
            {withSuspense(<AgentList />)}
          </RoleRoute>
        ),
      },
      {
        path: 'admin/agents/cards',
        element: (
          <RoleRoute roles={[ROLE.ADMIN]}>
            {withSuspense(<AgentRechargeCards />)}
          </RoleRoute>
        ),
      },
      {
        path: 'admin/agents/types',
        element: (
          <RoleRoute roles={[ROLE.ADMIN]}>
            {withSuspense(<AgentCardTypes />)}
          </RoleRoute>
        ),
      },
      {
        path: 'admin/agents/announcement',
        element: (
          <RoleRoute roles={[ROLE.ADMIN]}>
            {withSuspense(<AgentAnnouncement />)}
          </RoleRoute>
        ),
      },
    ],
  },

  { path: '*', element: withSuspense(<NotFound />) },
]);
